/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/10/22, 8:10 PM
 *
 */

import React, { Fragment, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import moment from 'moment';
import { Member } from 'types/MemberTypes';
import { MemberDetailCategory, Role } from 'types/RoleTypes';

import { useAddPatient, useNestedAddPatient } from 'features/members/api/query/MemberAddQuery';
import { useGetOtherFieldsList } from 'features/members/api/query/useGetOtherFieldsList';
import { updateUserProfile } from 'features/members/api/requests/member.request';

import { ProvinceDropdown, SwitchInput } from '@sunyahealthnepal/react-ui-components';
import { alert, toastAlert } from 'components/Alert';
import Button from 'components/Button';
import DropdownController from 'components/DropdownController';
import Input from 'components/Input';
import { Modal } from 'components/Modal/useModal';

interface UserAddFormData {
  name: string;
  address: string;
  phone: string;
  email: string;
  password: string;
  dob_ad: number;
  dob_bs: number;
  gender: 'Male' | 'Female' | 'Others';
  marital_status: 'Single' | 'Married';
}

interface UserAddFormProps
  extends Required<
    Pick<
      UseFormReturn<any>,
      'register' | 'handleSubmit' | 'control' | 'reset' | 'watch' | 'resetField' | 'setValue'
    >
  > {
  type?: 'edit' | 'add';
  initialData?: Member;
  selectedRole: Role;
  parent_member_id?: number;
}

export const MemberAddEditForm: React.FC<UserAddFormProps> = ({
  type = 'add',
  handleSubmit,
  control,
  register,
  reset,
  initialData,
  setValue,
  selectedRole,
  parent_member_id,
  resetField,
  watch,
}) => {
  useGetOtherFieldsList();
  const { mutateAsync: mutate } = useAddPatient();

  const { mutateAsync: nestedmutate } = useNestedAddPatient(parent_member_id ?? 0);

  const member_detail_categories =
    selectedRole.member_detail_categories && selectedRole.member_detail_categories;

  const dob = watch('dob_ad');

  useEffect(() => {
    setValue('age', moment(new Date()).diff(dob, 'years'));
  }, [dob]);

  return (
    <Modal.Form
      onSubmit={handleSubmit<UserAddFormData>(async (data: any) => {
        const body = {
          ...data,
          dob_ad: moment(data.dob_ad).unix(),
          role_id: selectedRole.id,
          parent_member_id,
        };

        const values = Object.values(body);
        const keys = Object.keys(body);

        const requestBody: Record<string, unknown>[] = [];
        requestBody.push(
          ...keys.map((element, index) => {
            if (isNaN(Number(element[0]))) {
              return { [element]: values[index] };
            }

            return {
              [`${element[0]}-detail`]: {
                detail_category_id: Number(element.split('-')[0]),
                value: values[index],
              },
            };
          })
        );

        const finalBody = requestBody.reduce((acc: any, curr) => {
          if (isNaN(Number(Object.keys(curr)[0][0]))) {
            acc = { ...acc, ...curr };
          } else if (acc.details) {
            acc.details.push(Object.values(curr)[0]);
          } else {
            acc.details = [Object.values(curr)[0]];
          }

          return acc;
        }, {});

        parent_member_id
          ? await toastAlert({
              type: 'promise',
              promise: nestedmutate(finalBody).then(() => reset()),
              msgs: {
                loading: 'Adding Member',
                success: 'Added Successfully',
              },
              id: 'patient-add-toast',
            })
          : type === 'add'
          ? await toastAlert({
              type: 'promise',
              promise: mutate(finalBody).then(() => reset()),
              msgs: {
                loading: 'Adding Member',
                success: 'Added Successfully',
              },
              id: 'patient-add-toast',
            })
          : initialData &&
            (await alert({
              promise: updateUserProfile(initialData.id, {
                ...data,
                dob_ad: moment(data.dob_ad).unix(),
              }),
              msgs: {
                loading: 'Updating Member',
                success: 'Updated Successfully',
              },
              id: 'member-update-toast',
            }));
      })}
    >
      <Modal.Scrollable>
        <div className="space-y-8">
          <Input
            label="Name"
            type="text"
            placeholder="Enter Name"
            required={true}
            {...register('name')}
          />

          {type === 'add' ? (
            <Input label="Phone" type="number" placeholder="Enter Phone" {...register('phone')} />
          ) : null}

          <div className="flex gap-x-6">
            <div className="w-1/2">
              <Input
                label="Date of Birth In AD"
                type="date"
                max={new Date().toISOString().split('T')[0]}
                {...register('dob_ad')}
              />
            </div>
            <div className="w-1/2">
              <Input label="Age" type="number" placeholder="Enter Age" {...register('age')} />
            </div>
          </div>

          <div className="flex gap-x-6">
            <div className="w-1/2">
              <DropdownController
                name="gender"
                label="Enter Gender"
                control={control}
                options={[
                  {
                    value: 'Male',
                    label: 'Male',
                  },
                  {
                    value: 'Female',
                    label: 'Female',
                  },
                  {
                    value: 'Others',
                    label: 'Others',
                  },
                  {
                    value: 'Not-Specified',
                    label: 'Not Specified',
                  },
                ]}
              />
            </div>
            <div className="w-1/2">
              <DropdownController
                name="marital_status"
                label="Enter Marital Status"
                control={control}
                options={[
                  {
                    value: 'Single',
                    label: 'Single',
                  },
                  {
                    value: 'Married',
                    label: 'Married',
                  },
                  {
                    value: 'Not-Specified',
                    label: 'Not Specified',
                  },
                ]}
              />
            </div>
          </div>

          <ProvinceDropdown control={control} watch={watch} resetField={resetField} />

          <Input
            label="Street Address"
            type="text"
            placeholder="Enter Street Address"
            {...register('address')}
          />

          {type === 'add' && (
            <Input label="Email" type="email" placeholder="Enter email" {...register('email')} />
          )}

          {type === 'add' &&
            selectedRole &&
            member_detail_categories.map((category: MemberDetailCategory) => {
              return (
                <Fragment key={category.id}>
                  {category.value_type?.toLowerCase() === 'boolean' ? (
                    <SwitchInput
                      label={category.name}
                      type="number"
                      placeholder={`Enter ${category.name}`}
                      {...register(`${category.id}-${category.slug}-details`)}
                    />
                  ) : (
                    <Input
                      label={category.name}
                      type={category.value_type}
                      required={!!category.required}
                      placeholder={`Enter ${category.name}`}
                      {...register(`${category.id}-${category.slug}-details`)}
                    />
                  )}
                </Fragment>
              );
            })}
        </div>
      </Modal.Scrollable>
      <div className="px-2">
        <Button> {type === 'add' ? 'Add' : 'Edit'} User</Button>
      </div>
    </Modal.Form>
  );
};
